import React, { useEffect, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
	Box,
	Breadcrumbs,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	Link,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	makeStyles,
} from "@material-ui/core";
import {
	FilterList,
	KeyboardBackspace,
	NavigateNext,
} from "@material-ui/icons";
import { safeDateFormat } from "../utils";
import { getAdjournments } from "../service";
import {
	STATUS_OPTIONS,
	buildPartyLabels,
	nextHearingText,
	requesterInfo,
	statusStyle,
} from "./adjournmentUtils";

const useStyles = makeStyles((theme) => ({
	paperCard: {
		padding: "2rem",
		border: "1px solid #E5E5E5",
		borderRadius: "4px",
		marginBottom: "2rem",
		[theme.breakpoints.down("sm")]: {
			padding: "1rem",
		},
	},
	breadcrumbs: {
		fontSize: "0.9rem",
		"& a": {
			cursor: "pointer",
			color: theme.palette.primary.main,
			fontWeight: 600,
		},
	},
	heading: {
		fontWeight: 600,
		fontSize: "1.5rem",
		lineHeight: "1.8rem",
		color: "black",
	},
	tableContainer: {
		borderRadius: 8,
		border: "1px solid #E5E5E5",
		boxShadow: "none",
		marginTop: 0,
		maxWidth: "100%",
		overflowX: "auto",
	},
	tableHeaderCell: {
		fontWeight: 600,
		backgroundColor: "#FAFAFB",
		color: TEXT.primary,
		fontSize: "0.85rem",
		padding: "0.62rem",
		whiteSpace: "nowrap",
		borderBottom: "1px solid #E5E5E5",
	},
	tableCell: {
		fontSize: "0.875rem",
		color: TEXT.primary,
		padding: "0.62rem",
		verticalAlign: "top",
		borderBottom: "1px solid #E5E5E5",
		overflowWrap: "anywhere",
		wordBreak: "break-word",
	},
	// Long headings wrap onto two lines instead of widening their column.
	headerCellWrap: {
		whiteSpace: "normal",
	},
	nextDateHeaderCell: {
		minWidth: "8rem",
		maxWidth: "12rem",
	},
	reasonCell: {
		minWidth: "14rem",
		maxWidth: "40rem",
	},
	filterButton: {
		textTransform: "none",
		fontWeight: 600,
		borderColor: "#E5E5E5",
		color: TEXT.strong,
	},
	emptyState: {
		padding: "3rem",
		textAlign: "center",
		color: TEXT.muted,
	},
}));

export default function AdjournmentRequests({ disputeId, allProfiles, onBack }) {
	const classes = useStyles();
	const partyLabels = buildPartyLabels(allProfiles);

	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	// Status filter — empty means "no filter", i.e. show every request.
	const [statuses, setStatuses] = useState([]);
	const [filterAnchor, setFilterAnchor] = useState(null);

	useEffect(() => {
		setLoading(true);
		setError("");
		getAdjournments(disputeId)
			.then((res) => {
				const data = res.data || {};
				setItems(Array.isArray(data.adjournments) ? data.adjournments : []);
			})
			.catch(() => setError("Failed to load adjournments."))
			.finally(() => setLoading(false));
	}, [disputeId]);

	const toggleStatus = (status) => {
		setStatuses((prev) =>
			prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
		);
	};

	const visibleItems = statuses.length
		? items.filter((adj) => statuses.includes(adj.status))
		: items;

	return (
		<Paper className={classes.paperCard}>
			{/* Breadcrumb — back to the case's Adjournments card */}
			<Box display="flex" alignItems="center" style={{ gap: "10px" }}>
				<IconButton size="small" onClick={onBack}>
					<KeyboardBackspace />
				</IconButton>
				<Breadcrumbs
					separator={<NavigateNext fontSize="small" />}
					aria-label="breadcrumb"
					className={classes.breadcrumbs}
				>
					<Link onClick={onBack}>Adjournments</Link>
					<Typography color="textPrimary" style={{ fontSize: "0.9rem" }}>
						Previous Requests
					</Typography>
				</Breadcrumbs>
			</Box>

			<Box height="1rem" />

			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				style={{ gap: "1rem", flexWrap: "wrap" }}
			>
				<Typography className={classes.heading}>Adjournment Requests</Typography>
				<Box display="flex" alignItems="center" style={{ gap: "0.75rem" }}>
					{statuses.length > 0 && (
						<Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
							Showing {visibleItems.length} of {items.length}
						</Typography>
					)}
					<Button
						variant="outlined"
						size="small"
						className={classes.filterButton}
						startIcon={<FilterList />}
						onClick={(e) => setFilterAnchor(e.currentTarget)}
						aria-haspopup="true"
					>
						Filter{statuses.length > 0 ? ` (${statuses.length})` : ""}
					</Button>
					<Menu
						anchorEl={filterAnchor}
						keepMounted
						open={Boolean(filterAnchor)}
						onClose={() => setFilterAnchor(null)}
						getContentAnchorEl={null}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<Box style={{ padding: "0 16px 4px" }}>
							<Typography style={{ fontSize: "0.75rem", color: TEXT.muted, fontWeight: 600 }}>
								STATUS
							</Typography>
						</Box>
						{STATUS_OPTIONS.map((status) => (
							<MenuItem key={status} onClick={() => toggleStatus(status)} dense>
								<Checkbox
									checked={statuses.includes(status)}
									color="primary"
									size="small"
									style={{ padding: "0 8px 0 0" }}
								/>
								<ListItemText
									primary={statusStyle(status).label}
									primaryTypographyProps={{ style: { fontSize: "0.85rem" } }}
								/>
							</MenuItem>
						))}
						<MenuItem
							dense
							disabled={statuses.length === 0}
							onClick={() => setStatuses([])}
							style={{ borderTop: "1px solid #E5E5E5", marginTop: "4px" }}
						>
							<ListItemText
								primary="Clear filter"
								primaryTypographyProps={{ style: { fontSize: "0.85rem" } }}
							/>
						</MenuItem>
					</Menu>
				</Box>
			</Box>

			{statuses.length > 0 && (
				<Box display="flex" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
					{statuses.map((status) => {
						const st = statusStyle(status);
						return (
							<Chip
								key={status}
								label={st.label}
								size="small"
								onDelete={() => toggleStatus(status)}
								style={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }}
							/>
						);
					})}
				</Box>
			)}

			<Box height="1rem" />

			{error && (
				<Typography style={{ color: TEXT.error, fontSize: "0.85rem", marginBottom: "0.75rem" }}>
					{error}
				</Typography>
			)}

			{loading ? (
				<Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
					<CircularProgress size={16} />
					<Typography style={{ fontSize: "0.85rem", color: TEXT.muted }}>
						Loading adjournments…
					</Typography>
				</Box>
			) : visibleItems.length === 0 ? (
				<Typography className={classes.emptyState}>
					{items.length === 0
						? "No adjournment requests yet."
						: "No adjournment requests match the selected status."}
				</Typography>
			) : (
				<TableContainer component={Paper} className={classes.tableContainer}>
					<Table size="small" style={{ minWidth: "100%", marginTop: 0 }}>
						<TableHead>
							<TableRow>
								<TableCell className={classes.tableHeaderCell}>Requested By</TableCell>
								<TableCell className={classes.tableHeaderCell}>Requested On</TableCell>
								<TableCell className={classes.tableHeaderCell}>Hearing in Question</TableCell>
								<TableCell className={`${classes.tableHeaderCell} ${classes.headerCellWrap}`}>
									Reasons for Adjournment
								</TableCell>
								<TableCell
									className={`${classes.tableHeaderCell} ${classes.headerCellWrap} ${classes.nextDateHeaderCell}`}
								>
									Next Hearing Date fixed by Arbitral Tribunal
								</TableCell>
								<TableCell className={classes.tableHeaderCell}>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{visibleItems.map((adj) => {
								const st = statusStyle(adj.status);
								const requester = requesterInfo(adj, partyLabels);
								return (
									<TableRow key={adj.id}>
										<TableCell className={classes.tableCell}>
											<Typography style={{ fontSize: "0.85rem", fontWeight: 600 }}>
												{requester.role || requester.name}
											</Typography>
											{requester.role && (
												<Typography style={{ fontSize: "0.78rem", color: TEXT.muted }}>
													{requester.name}
												</Typography>
											)}
										</TableCell>
										<TableCell className={classes.tableCell}>
											{safeDateFormat(adj.created_at, "dd/MM/yyyy, HH:mm")}
										</TableCell>
										<TableCell className={classes.tableCell}>
											{adj.hearing_label || "—"}
										</TableCell>
										<TableCell className={`${classes.tableCell} ${classes.reasonCell}`}>
											{adj.reason || "—"}
										</TableCell>
										<TableCell className={classes.tableCell}>
											{nextHearingText(adj)}
										</TableCell>
										<TableCell className={classes.tableCell}>
											<Chip
												label={st.label}
												size="small"
												style={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }}
											/>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Paper>
	);
}
